<template>
  <div :class="$setClasses">
    <div v-if="field.title"
         class="u-display-flex u-ai-center u-margin-bottom-xsmall">
      <h2>{{ $te(field.title) ? $t(field.title) : field.title }}</h2>
    </div>
    <div v-if="isLoaded"
         v-loading="meetingsSlotsLoading || isDataSaving">
      <div v-if="field.content"
           v-html="field.content"
           :class="$vClass('content')"></div>
      <meeting-fields v-if="field?.fields?.dateTypeSelected"
                      v-model:dateTypeSelected="field.fields.dateTypeSelected.value"
                      v-model:description="field.fields.description.value"
                      v-model:duration="field.fields.duration.value"
                      v-model:language="field.fields.language.value"
                      v-model:meetingDate="field.fields.meetingDate.value"
                      v-model:meetingType="field.fields.meetingType.value"
                      v-model:operators="field.fields.operators.value"
                      v-model:scheduled="field.fields.scheduled.value"
                      :fields="field.fields || {}"
                      :key="`${field.fields.meetingType.value}-${field.fields.dateTypeSelected.value}`"
                      @update:dateTypeSelected="handlerDateTypeSelected"
                      @update:duration="handlerDuration"
                      @update:language="handlerLanguage"
                      @update:meetingType="handlerMeetingType"
                      @update:operators="handlerOperators">
        <meeting-display v-if="displaySaveMeeting"
                         :class="$vClass('meeting')"
                         :duration="duration"
                         :granularity="granularity"
                         :is-meeting-loading="meetingsSlotsLoading"
                         :meeting="formatMeeting()" />
      </meeting-fields>
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";
import { useMeetingStore } from "@nextories/vue-core-components/src/store/meetingStore.ts";
import { useProjectsStore } from "@nextories/vue-tasks/src/store/projectsStore.ts";
import { AppointmentBooking } from "@nextories/meeting-component";
import MeetingDisplay from "@/components/organisms/meeting/MeetingDisplay.vue";
import "@nextories/meeting-component/dist/lib/appointment-booking.css";
import { useUsersStore } from "@nextories/vue-tasks/src/store/usersStore.ts";
import MeetingFields from "@/components/organisms/meeting/MeetingFields.vue";
import { useAuthStore } from "@nextories/vue-tasks/src/store/authStore.ts";

export default {
  name: "Meeting",
  nameClassStyle: "appointment",
  components: {
    MeetingFields,
    AppointmentBooking,
    MeetingDisplay,
  },
  props: {
    field: Object,
    prefix: String,
  },
  data() {
    return {
      isLoaded: false,
      visitTypeClassic: 'VISIT_TYPE_CLASSIC',
      visitTypeUnset: 'VISIT_TYPE_UNSET',
    }
  },
  computed: {
    ...mapState(useMeetingStore, {
      durationStore: 'duration',
      meetingDateStore: 'meetingDate',
      operatorsStore: 'operators',
      descriptionStore: 'description',
      meetingTypeStore: 'typeMeeting',
    }),
    ...mapState(useProjectsStore, ["savedApiResponse", "project"]),
    ...mapState(useMeetingStore, ["meetingsSlotsLoading", "granularity", "lock", "isDataSaving"]),
    ...mapState(useUsersStore, ["users"]),
    ...mapState(useAuthStore, ["currentUser"]),
    fields() {
      return (this.field && this.field.fields) || {};
    },
    alreadyMeeting() {
      return !!(this.project && this.project.meeting);
    },
    language() {
      return this.fields?.language?.value;
    },
    dateTypeSelected() {
      return this.fields?.dateTypeSelected?.value;
    },
    operators() {
      return this.fields?.operators?.value ? Array.isArray(this.fields.operators.value) ? this.fields.operators.value : [this.fields.operators.value] : [];
    },
    meetingDate() {
      return this.fields?.meetingDate?.value;
    },
    duration() {
      return this.fields?.duration?.value;
    },
    scheduled() {
      return this.fields?.scheduled?.value;
    },
    meetingType() {
      return this.fields?.meetingType?.value;
    },
    displayScheduled() {
      return this.dateTypeSelected === 'scheduled';
    },
    isMeetingVad() {
      return this.meetingType === this.visitTypeClassic;
    },
    isMeetingUnset() {
      return this.meetingType === this.visitTypeUnset;
    },
    displaySaveMeeting() {
      if (this.displayScheduled) {
        // Return false if scheduled is empty and scheduled loading
        return !!(this.scheduled && Object.keys(this.scheduled)?.length) && !this.fields?.scheduled?.loading;
      }
      // If dateTypeSelected is null or not operators selected
      if (!this.dateTypeSelected || (this.field?.fields?.operators?.required && !this.operators?.length)) {
        return false;
      }
      if ('date-time' === this.dateTypeSelected) {
        return true;
      }
      return !!(this.meetingDate && this.meetingDate !== this.meetingDateStore);
    },
    contentDate() {
      return this.$t(`meeting.${this.durationStore ? 'duration' : 'date'}`, {
        date: this.$filters.date.formatDateCustom(this.meetingDateStore, 'DD/MM/YYYY HH:mm'),
        duration: this.durationStore,
      });
    },
    currentMeetingOperator() {
      const itemApiMeeting = this.savedApiResponse?.api_meeting;
      return (this.operatorsStore?.length)
        ? this.operatorsStore
        : ((itemApiMeeting?.operators) || []);
    },
    canAccessChangeOperator() {
      return this.$helpers.authorisation.hasAccessRight(this.currentUser, 'meetings','operator', true);
    },
  },
  async created() {
    try {
      // Get values from savedApiResponse
      const itemApiMeeting = this.savedApiResponse?.api_meeting;
      const meetingType = (this.fields?.meetingType?.value) || this.meetingTypeStore || (itemApiMeeting?.meetingType) || '';
      let dateTypeSelected = itemApiMeeting?.dateTypeSelected;
      if (!this.alreadyMeeting && this.meetingTypeStore) {
        dateTypeSelected = this.meetingTypeStore === this.visitTypeClassic ? 'date-time' : 'scheduled';
      } else {
        dateTypeSelected = meetingType === this.visitTypeClassic || this.alreadyMeeting ? 'date-time' : 'scheduled';
      }
      const operators = this.currentMeetingOperator;
      const isScheduled = dateTypeSelected === 'scheduled';
      const isDateTime = dateTypeSelected === 'date-time';
      const isMeetingEditor = this.canAccessChangeOperator || [this.meetingTypeStore || meetingType].some((value) => value === this.visitTypeClassic);
      // Merge form config -> field.fields
      this.field.fields = {
        language: {
          value: (itemApiMeeting?.language) || 'FR',
          ...this.getField('language'),
        },
        operators: {
          ...this.getField('operators'),
          multiple: isScheduled,
          disabled: !isMeetingEditor,
          value: isScheduled ? operators : operators && operators[0],
          required: dateTypeSelected === 'date-time' || meetingType === this.visitTypeClassic,
        },
        duration: {
          value: this.alreadyMeeting
            ? (this.durationStore || itemApiMeeting?.duration || 45)
            : undefined,
          ...this.getField('duration'),
        },
        description: {
          value: this.descriptionStore || itemApiMeeting?.description,
          ...this.getField('description'),
        },
        vadContent: {
          hide: meetingType !== this.visitTypeClassic,
          ...this.getField('vadContent'),
        },
        meetingDate: {
          hide: !isDateTime,
          value: this.meetingDateStore || itemApiMeeting?.meetingDate || new Date(),
          display_name: (!this.alreadyMeeting || meetingType !== this.visitTypeClassic) ? this.$tm(`meeting.label.meetingDate`) : this.contentDate,
          disabled: !isMeetingEditor,
          ...this.getField('meetingDate'),
        },
        dateTypeSelected: {
          value: (this.getField('dateTypeSelected') && this.getField('dateTypeSelected').value) || dateTypeSelected,
          ...this.getField('dateTypeSelected'),
          hide: typeof this.fields?.dateTypeSelected?.hide === 'boolean'
            ? this.fields.dateTypeSelected.hide
            : (!this.alreadyMeeting || [this.visitTypeUnset, this.visitTypeClassic].some(type => type === meetingType)),
          display_name: this.contentDate,
        },
        meetingType: {
          value: meetingType,
          ...this.getField('meetingType'),
        },
        scheduled: {
          hide: this.isMeetingUnset || this.visitTypeUnset === meetingType || isDateTime,
          loading: false,
          value: {},
          ...this.getField('scheduled'),
        },
      };
      this.isLoaded = true;
    } catch (error) {
      console.error(error);
    }
  },
  methods: {
    formatMeeting() {
      const mapOperators = this.operators && this.operators.map((operator) => ({
        operator: (operator && operator.address_state) || operator,
        score: 1,
      }));
      // If not display scheduled
      //  get operator in input select operators
      // else
      //  get operator in scheduled
      const getOperator = !this.displayScheduled
        ? this.operators && this.operators[0]
        : this.lock?.operator?.address_state;
      return this.$helpers.meeting.formatMeeting({
        ...this.alreadyMeeting && {
          old_date: this.meetingDateStore,
        },
        ...this.dateTypeSelected === 'date-time' ? {
          language: this.fields.language.value || 'FR',
          duration: this.duration || this.durationStore,
        } : {},
        visit_type: this.meetingType,
        // Send param for meeting VAD
        ...!this.displayScheduled && this.meetingDate ? {
          duration: this.duration,
          start: this.$filters.date.formatDateCustom(this.meetingDate, 'YYYY-MM-DDTHH:mm:ssZ'),
          end: this.$filters.date.formatDuration(this.meetingDate, 'minutes', this.duration),
          operators: mapOperators || undefined,
        } : {
          ...this.fields.scheduled.value,
          operators: this.fields?.scheduled?.value?.operators?.length ? [this.fields.scheduled.value.operators[0]] : undefined,
        },
        description: this.fields.description.value || undefined,
      }, getOperator,
        this.users, true);
    },
    handlerDateTypeSelected(value) {
      this.field.fields.meetingDate.hide = value !== 'date-time';
      this.field.fields.scheduled.hide = value !== 'scheduled';
      if (value) {
        this.field.fields.meetingDate.value = this.meetingDateStore;
      }
      // Format operators field when date type selected === date-time
      const isDateTime = value === 'date-time';
      let operators = this.operators;
      if (operators) {
        if (Array.isArray(operators)) {
          if (isDateTime) {
            if (!operators.length) {
              operators = this.currentMeetingOperator;
            }
            operators = operators.length > 1 ? '' : operators[0];
          } else {
            operators = '';
          }
        } else if (!isDateTime) {
          operators = [operators];
        }
      }
      this.field.fields.operators = {
        ...this.field.fields.operators,
        multiple: !isDateTime,
        value: operators,
        required: isDateTime || this.isMeetingVad,
      };
      this.field.fields.scheduled.loading = false;
    },
    handlerDuration() {
      this.field.fields.scheduled.loading = true;
    },
    handlerLanguage() {
      this.field.fields.scheduled.loading = true;
    },
    handlerMeetingType(value) {
      let hideScheduled = true;
      let isMeetingEditor = this.isMeetingVad || this.canAccessChangeOperator;
      if (this.alreadyMeeting || this.isMeetingUnset) {
        // Initialize fields when change meeting type
        if (this.isMeetingVad) {
          // Config when is meeting vad
          this.field.fields.dateTypeSelected = {
            ...this.field.fields.dateTypeSelected,
            hide: true,
            value: 'date-time',
          };
          this.field.fields.meetingDate.display_name = this.contentDate;
        } else {
          // Config when is other
          this.field.fields.dateTypeSelected = {
            ...this.field.fields.dateTypeSelected,
            hide: this.isMeetingUnset || !value,
            value: undefined,
          };
          this.field.fields.meetingDate.display_name = this.$tm(`meeting.label.meetingDate`);
        }
      } else {
        // If meeting type exist
        // set hide or not fields
        this.field.fields.meetingDate.display_name = this.$tm(`meeting.label.meetingDate`);
        if (value) {
          this.field.fields.dateTypeSelected.value = this.isMeetingVad ? 'date-time' : 'scheduled';
          hideScheduled = this.isMeetingVad;
        }
      }
      this.field.fields.meetingDate.disabled = !isMeetingEditor;
      this.field.fields.operators.disabled = !isMeetingEditor;
      this.field.fields.operators.required = this.field.fields.dateTypeSelected.value === 'date-time' || this.isMeetingVad;
      this.field.fields.meetingDate.hide = !this.isMeetingVad;
      this.field.fields.vadContent.hide = !this.isMeetingVad;
      this.field.fields.scheduled.hide = hideScheduled;
      this.field.fields.operators.value = this.operatorsStore;
      this.field.fields.scheduled.loading = true;
      if (!this.field.fields.meetingDate.hide) {
        this.handlerDateTypeSelected('date-time');
      }
    },
    handlerOperators() {
      this.field.fields.scheduled.loading = true;
    },
    getField(key) {
      return {
        id: `api_meeting.${key}`,
        ...this.fields && this.fields[key],
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.#{$prefixName}appointment {
  display: block;

  h2 {
    margin: 0;
  }

  &__content {
    margin: var(--nxt-space-large) 0;
  }
}
</style>
